import { useCallback, useEffect, useRef, useState } from "react";
import {
  calculatePixelsPerMillimeter,
  denormalizePoint,
  distance,
} from "../lib/cranialGeometry";

export const EDITOR_MODES = ["landmarks", "contour", "scale", "pan"];

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 6;
export const MIN_CONTOUR_POINTS = 8;
export const MIN_SCALE_DISTANCE_PX = 10;

const MAX_HISTORY = 50;
const COALESCE_WINDOW_MS = 1000;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const clampPoint = (p) => ({ x: clamp(p.x, 0, 1), y: clamp(p.y, 0, 1) });

export const EMPTY_CALIBRATION = {
  status: "not_detected",
  source: null,
  markerType: null,
  referenceSizeMm: null,
  referenceSizePx: null,
  pixelsPerMillimeter: null,
  confidence: null,
  points: [],
};

/**
 * Recomputes the derived calibration fields (README 3.8) after a change.
 * `manual` marks the calibration as edited by the user (status 'manual', source 'manual').
 * Status is 'invalid' when points exist but the scale cannot be computed
 * (missing second point, size <= 0 or points closer than MIN_SCALE_DISTANCE_PX).
 */
export function resolveCalibration(base, patch, imageWidth, imageHeight, { manual = true } = {}) {
  const next = { ...EMPTY_CALIBRATION, ...(base || {}), ...(patch || {}) };
  const points = Array.isArray(next.points) ? next.points : [];
  const sizeMm = Number(next.referenceSizeMm);

  if (points.length === 0) {
    return {
      ...next,
      points: [],
      status: "not_detected",
      source: null,
      referenceSizePx: null,
      pixelsPerMillimeter: null,
      confidence: null,
    };
  }

  const source = manual ? "manual" : next.source;
  const confidence = manual ? null : next.confidence;

  let referenceSizePx = null;
  if (points.length >= 2) {
    const a = denormalizePoint(points[0], imageWidth, imageHeight);
    const b = denormalizePoint(points[1], imageWidth, imageHeight);
    const d = a && b ? distance(a, b) : NaN;
    referenceSizePx = Number.isFinite(d) ? d : null;
  }

  const valid =
    points.length >= 2 &&
    sizeMm > 0 &&
    referenceSizePx !== null &&
    referenceSizePx >= MIN_SCALE_DISTANCE_PX;

  if (!valid) {
    return {
      ...next,
      points,
      status: "invalid",
      source,
      confidence,
      referenceSizePx,
      pixelsPerMillimeter: null,
    };
  }

  const keepDetected = !manual && next.status === "detected";
  return {
    ...next,
    points,
    status: keepDetected ? "detected" : "manual",
    source,
    confidence,
    referenceSizeMm: sizeMm,
    referenceSizePx,
    pixelsPerMillimeter: calculatePixelsPerMillimeter(points, sizeMm, imageWidth, imageHeight),
  };
}

/** Squared distance from p to segment ab (pixel space). */
function segmentDistanceSq(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq > 0 ? clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq, 0, 1) : 0;
  const cx = a.x + t * dx - p.x;
  const cy = a.y + t * dy - p.y;
  return cx * cx + cy * cy;
}

/** Index i of the closed-polygon segment (i -> i+1) nearest to the point. */
export function findNearestSegmentIndex(contour, point, imageWidth, imageHeight) {
  const w = imageWidth || 1;
  const h = imageHeight || 1;
  const p = { x: point.x * w, y: point.y * h };
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < contour.length; i++) {
    const a = contour[i];
    const b = contour[(i + 1) % contour.length];
    const d = segmentDistanceSq(p, { x: a.x * w, y: a.y * h }, { x: b.x * w, y: b.y * h });
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/**
 * Editor-local state for CranialEditor: active mode, selection, zoom/pan and undo/redo.
 * Geometry and calibration stay owned by the parent (controlled); every change is emitted
 * through onGeometryChange / onCalibrationChange. Drags emit live (so measurements update
 * in real time) but record a single history entry on drag end.
 *
 * Drag targets: { kind: 'contour', index }
 * | { kind: 'landmark', key: 'nose'|'earLeft'|'earRight'|'posterior' } | { kind: 'scale', index }.
 * The axis, A and the width are derived from the landmarks by computeMeasurements, so they
 * have no drag target of their own. P (posterior) is automatic while landmarks.posterior is
 * null; the canvas draws it at the derived point and the first drag stores the dragged
 * position in landmarks.posterior (one history entry). resetPosterior sets it back to null.
 * Pan is stored in viewport fractions (stage offset), zoom in 1..6.
 */
export default function useCranialEditor({
  geometry,
  calibration,
  imageWidth,
  imageHeight,
  onGeometryChange,
  onCalibrationChange,
  onRestoreSuggestion,
}) {
  const [mode, setModeState] = useState("contour");
  const [selectedPointIndex, setSelectedPointIndex] = useState(null);
  const [view, setView] = useState({ zoom: 1, pan: { x: 0, y: 0 } });
  const [history, setHistory] = useState({ past: [], future: [] });

  // Latest values for pointer handlers, which can fire faster than re-renders.
  const latest = useRef({ geometry, calibration });
  latest.current = { geometry, calibration };
  const dragRef = useRef(null);
  const coalesceRef = useRef({ key: null, at: 0 });

  const snapshot = useCallback(
    () => ({ geometry: latest.current.geometry, calibration: latest.current.calibration }),
    []
  );

  const pushHistory = useCallback((entry, coalesceKey = null) => {
    const now = Date.now();
    const last = coalesceRef.current;
    const coalesce = coalesceKey && last.key === coalesceKey && now - last.at < COALESCE_WINDOW_MS;
    coalesceRef.current = { key: coalesceKey, at: now };
    if (coalesce) return;
    setHistory((h) => ({ past: [...h.past, entry].slice(-MAX_HISTORY), future: [] }));
  }, []);

  const emitGeometry = useCallback(
    (patch) => {
      const next = { ...latest.current.geometry, ...patch, wasManuallyEdited: true };
      latest.current = { ...latest.current, geometry: next };
      if (onGeometryChange) onGeometryChange(next);
    },
    [onGeometryChange]
  );

  const emitCalibration = useCallback(
    (next) => {
      latest.current = { ...latest.current, calibration: next };
      if (onCalibrationChange) onCalibrationChange(next);
    },
    [onCalibrationChange]
  );

  // Keep the selection valid when the contour shrinks (undo, restore).
  const contourLength = geometry?.cranialContour?.length || 0;
  useEffect(() => {
    if (selectedPointIndex !== null && selectedPointIndex >= contourLength) {
      setSelectedPointIndex(null);
    }
  }, [contourLength, selectedPointIndex]);

  const setMode = useCallback((next) => {
    if (!EDITOR_MODES.includes(next)) return;
    setModeState(next);
    if (next !== "contour") setSelectedPointIndex(null);
  }, []);

  /** Live move of a drag target, without a history entry. */
  const movePoint = useCallback(
    (target, point) => {
      if (!target || !point) return;
      const p = clampPoint(point);
      const { geometry: g, calibration: c } = latest.current;
      if (target.kind === "contour") {
        const contour = [...(g?.cranialContour || [])];
        if (target.index < 0 || target.index >= contour.length) return;
        contour[target.index] = p;
        emitGeometry({ cranialContour: contour });
      } else if (target.kind === "landmark") {
        const landmarks = { ...(g?.landmarks || {}) };
        landmarks[target.key] = { ...(landmarks[target.key] || {}), x: p.x, y: p.y };
        emitGeometry({ landmarks });
      } else if (target.kind === "scale") {
        const points = [...(c?.points || [])];
        if (target.index < 0 || target.index >= points.length) return;
        points[target.index] = p;
        emitCalibration(resolveCalibration(c, { points }, imageWidth, imageHeight));
      }
    },
    [emitGeometry, emitCalibration, imageWidth, imageHeight]
  );

  const beginDrag = useCallback(
    (target) => {
      dragRef.current = { target, before: snapshot(), moved: false };
      if (target?.kind === "contour") setSelectedPointIndex(target.index);
    },
    [snapshot]
  );

  const dragTo = useCallback(
    (target, point) => {
      if (dragRef.current) dragRef.current.moved = true;
      movePoint(target, point);
    },
    [movePoint]
  );

  const endDrag = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && drag.moved) pushHistory(drag.before);
  }, [pushHistory]);

  const selectPoint = useCallback((index) => {
    setSelectedPointIndex(Number.isInteger(index) ? index : null);
  }, []);

  /** Inserts a point after segment `index` (or after the nearest segment when index is null). */
  const addContourPoint = useCallback(
    (index, point) => {
      const contour = latest.current.geometry?.cranialContour || [];
      if (!point || contour.length < 2) return;
      const after =
        Number.isInteger(index) && index >= 0 && index < contour.length
          ? index
          : findNearestSegmentIndex(contour, point, imageWidth, imageHeight);
      pushHistory(snapshot());
      const next = [...contour];
      next.splice(after + 1, 0, clampPoint(point));
      emitGeometry({ cranialContour: next });
      setSelectedPointIndex(after + 1);
    },
    [emitGeometry, pushHistory, snapshot, imageWidth, imageHeight]
  );

  /** Removes a contour point (default: the selected one), keeping at least MIN_CONTOUR_POINTS. */
  const removeContourPoint = useCallback(
    (index = selectedPointIndex) => {
      const contour = latest.current.geometry?.cranialContour || [];
      if (!Number.isInteger(index) || index < 0 || index >= contour.length) return;
      if (contour.length <= MIN_CONTOUR_POINTS) return;
      pushHistory(snapshot());
      emitGeometry({ cranialContour: contour.filter((_, i) => i !== index) });
      setSelectedPointIndex(null);
    },
    [emitGeometry, pushHistory, snapshot, selectedPointIndex]
  );

  /** Committed landmark placement (one history entry). */
  const setLandmark = useCallback(
    (key, point) => {
      pushHistory(snapshot());
      movePoint({ kind: "landmark", key }, point);
    },
    [movePoint, pushHistory, snapshot]
  );

  /** Back to the automatic P (landmarks.posterior = null), one history entry. */
  const resetPosterior = useCallback(() => {
    const landmarks = latest.current.geometry?.landmarks || {};
    if (!landmarks.posterior) return;
    pushHistory(snapshot());
    emitGeometry({ landmarks: { ...landmarks, posterior: null } });
  }, [emitGeometry, pushHistory, snapshot]);

  /** Sets scale point `index` (0 or 1); an index equal to points.length appends. */
  const setScalePoint = useCallback(
    (index, point) => {
      const c = latest.current.calibration;
      const points = [...(c?.points || [])];
      if (!point || index < 0 || index > 1 || index > points.length) return;
      pushHistory(snapshot());
      if (index === points.length) points.push(clampPoint(point));
      else points[index] = clampPoint(point);
      emitCalibration(resolveCalibration(c, { points }, imageWidth, imageHeight));
    },
    [emitCalibration, pushHistory, snapshot, imageWidth, imageHeight]
  );

  /** Places the next missing scale point (tap on the image in scale mode). */
  const addScalePoint = useCallback(
    (point) => {
      const count = latest.current.calibration?.points?.length || 0;
      if (count < 2) setScalePoint(count, point);
    },
    [setScalePoint]
  );

  /** Full calibration replacement from CalibrationPanel; typing is coalesced by `coalesceKey`. */
  const updateCalibration = useCallback(
    (next, { coalesceKey = null } = {}) => {
      pushHistory(snapshot(), coalesceKey);
      emitCalibration(next);
    },
    [emitCalibration, pushHistory, snapshot]
  );

  const restoreSuggestion = useCallback(() => {
    if (!onRestoreSuggestion) return;
    pushHistory(snapshot());
    setSelectedPointIndex(null);
    onRestoreSuggestion();
  }, [onRestoreSuggestion, pushHistory, snapshot]);

  const applySnapshot = useCallback(
    (entry) => {
      latest.current = { ...latest.current, geometry: entry.geometry, calibration: entry.calibration };
      if (onGeometryChange) onGeometryChange(entry.geometry);
      if (onCalibrationChange) onCalibrationChange(entry.calibration);
    },
    [onGeometryChange, onCalibrationChange]
  );

  const undo = useCallback(() => {
    const { past, future } = history;
    if (past.length === 0) return;
    const entry = past[past.length - 1];
    setHistory({
      past: past.slice(0, -1),
      future: [snapshot(), ...future].slice(0, MAX_HISTORY),
    });
    coalesceRef.current = { key: null, at: 0 };
    applySnapshot(entry);
  }, [history, snapshot, applySnapshot]);

  const redo = useCallback(() => {
    const { past, future } = history;
    if (future.length === 0) return;
    const entry = future[0];
    setHistory({ past: [...past, snapshot()].slice(-MAX_HISTORY), future: future.slice(1) });
    coalesceRef.current = { key: null, at: 0 };
    applySnapshot(entry);
  }, [history, snapshot, applySnapshot]);

  /** Zooms by `factor` keeping `focal` (viewport fraction 0..1) fixed on screen. */
  const zoomBy = useCallback((factor, focal = { x: 0.5, y: 0.5 }) => {
    setView(({ zoom, pan }) => {
      const nextZoom = clamp(zoom * factor, MIN_ZOOM, MAX_ZOOM);
      const u = (focal.x - pan.x) / zoom;
      const v = (focal.y - pan.y) / zoom;
      return {
        zoom: nextZoom,
        pan: {
          x: clamp(focal.x - u * nextZoom, 1 - nextZoom, 0),
          y: clamp(focal.y - v * nextZoom, 1 - nextZoom, 0),
        },
      };
    });
  }, []);

  /** Pans by a delta in viewport fractions. */
  const panBy = useCallback((dx, dy) => {
    setView(({ zoom, pan }) => ({
      zoom,
      pan: {
        x: clamp(pan.x + dx, 1 - zoom, 0),
        y: clamp(pan.y + dy, 1 - zoom, 0),
      },
    }));
  }, []);

  const resetView = useCallback(() => setView({ zoom: 1, pan: { x: 0, y: 0 } }), []);
  const zoomIn = useCallback(() => zoomBy(1.25), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(1 / 1.25), [zoomBy]);

  return {
    mode,
    setMode,
    selectedPointIndex,
    selectPoint,
    zoom: view.zoom,
    pan: view.pan,
    zoomBy,
    zoomIn,
    zoomOut,
    panBy,
    resetView,
    beginDrag,
    dragTo,
    endDrag,
    movePoint,
    addContourPoint,
    removeContourPoint,
    canRemovePoint: selectedPointIndex !== null && contourLength > MIN_CONTOUR_POINTS,
    setLandmark,
    resetPosterior,
    canResetPosterior: Boolean(geometry?.landmarks?.posterior),
    setScalePoint,
    addScalePoint,
    updateCalibration,
    restoreSuggestion,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
}
